import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * A menu built from plain React, deliberately, rather than from
 * `@radix-ui/react-dropdown-menu`.
 *
 * This component is used by the app shell, so it lands in the entry bundle,
 * which is held to a 120 kB gzipped budget with roughly three kilobytes of
 * headroom. A dropdown primitive costs more than that on its own — and the
 * part of it this product uses is one popup, roving focus, and an outside
 * click, which is what follows.
 */

/** Closing is the item's job once it has been selected, so items need it. */
const MenuCloseContext = createContext<(() => void) | null>(null);

/**
 * The focusable items, read from the DOM rather than from a registry.
 *
 * Items are arbitrary children — sections, separators, conditionally rendered
 * entries — so a registration list has to be kept in sync with a tree the menu
 * does not own. Query order is document order, which is exactly the order the
 * arrow keys should follow.
 */
function focusableItems(popup: HTMLElement | null): HTMLElement[] {
  if (!popup) return [];
  return Array.from(
    popup.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([aria-disabled="true"])',
    ),
  );
}

export interface MenuProps {
  /** Rendered inside the trigger button — usually an icon, sometimes a label. */
  trigger: ReactNode;

  /** The trigger's accessible name, and the popup's. */
  label: string;

  /** Which edge of the trigger the popup is anchored to. */
  align?: "start" | "end";

  children: ReactNode;
}

export function Menu({ trigger, label, align = "start", children }: MenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const popupID = useId();

  // Set when the menu is opened from the keyboard, read once the popup has
  // mounted: there is nothing to focus until then.
  const pendingEdge = useRef<"first" | "last" | null>(null);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const closeAfterSelect = useCallback(() => {
    close(true);
  }, [close]);

  const focusEdge = useCallback((edge: "first" | "last") => {
    const items = focusableItems(popupRef.current);
    if (items.length === 0) return;
    items[edge === "first" ? 0 : items.length - 1].focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    if (pendingEdge.current === null) return;
    focusEdge(pendingEdge.current);
    pendingEdge.current = null;
  }, [open, focusEdge]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // The trigger's own click toggles; closing here too would reopen it.
      if (triggerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  function onTriggerClick(event: MouseEvent<HTMLButtonElement>) {
    // `detail === 0` is a click synthesised from Enter or Space, which is the
    // only reliable way to tell a keyboard activation from a pointer one.
    if (!open && event.detail === 0) pendingEdge.current = "first";
    setOpen((current) => !current);
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    // Opening by pointer leaves focus on the trigger, so the popup's own
    // handler never sees these: without this the only way out of a
    // mouse-opened menu is another click.
    if (open && event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (open && event.key === "Tab") {
      close(false);
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const edge = event.key === "ArrowDown" ? "first" : "last";
    if (open) {
      focusEdge(edge);
      return;
    }
    pendingEdge.current = edge;
    setOpen(true);
  }

  function onPopupKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === "Tab") {
      close(false);
      return;
    }

    const items = focusableItems(popupRef.current);
    if (items.length === 0) return;
    const current = items.indexOf(document.activeElement as HTMLElement);

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      const next = (current + step + items.length) % items.length;
      items[current === -1 && step === -1 ? items.length - 1 : next].focus();
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      items[0].focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      items[items.length - 1].focus();
      return;
    }
    if (event.key === " " && current !== -1) {
      // A link does not activate on Space by default, and an item that
      // responds to Enter but not Space reads as a broken control.
      event.preventDefault();
      items[current].click();
    }
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? popupID : undefined}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex min-h-8 min-w-8 items-center justify-center gap-1.5 rounded-md px-1.5 text-sm font-medium text-fg transition-colors hover:bg-bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none"
      >
        {trigger}
      </button>

      {open && (
        <div
          ref={popupRef}
          id={popupID}
          role="menu"
          aria-label={label}
          onKeyDown={onPopupKeyDown}
          className={cn(
            "absolute top-full z-30 mt-1 min-w-52 max-w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-border bg-bg-elevated p-1 shadow-lg",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          <MenuCloseContext.Provider value={closeAfterSelect}>
            {children}
          </MenuCloseContext.Provider>
        </div>
      )}
    </div>
  );
}

const menuItemVariants = cva(
  "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent motion-reduce:transition-none",
  {
    variants: {
      destructive: {
        true: "text-danger hover:bg-danger/10",
        false: "text-fg hover:bg-bg-sunken",
      },
      disabled: {
        true: "pointer-events-none text-fg-disabled",
        false: "",
      },
    },
    defaultVariants: { destructive: false, disabled: false },
  },
);

export interface MenuItemProps extends VariantProps<typeof menuItemVariants> {
  children: ReactNode;

  /** Run when the item is chosen. The menu closes afterwards either way. */
  onSelect?: () => void;

  /** Render a router link instead of a button. */
  to?: string;

  icon?: ReactNode;
}

export function MenuItem({
  children,
  onSelect,
  to,
  disabled = false,
  destructive = false,
  icon,
}: MenuItemProps) {
  const close = useContext(MenuCloseContext);
  const className = menuItemVariants({ destructive, disabled });
  const body = (
    <>
      {icon && (
        <span aria-hidden="true" className="shrink-0">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </>
  );

  function select() {
    onSelect?.();
    close?.();
  }

  if (to && !disabled) {
    return (
      <Link
        role="menuitem"
        tabIndex={-1}
        to={to}
        onClick={select}
        className={className}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      role="menuitem"
      tabIndex={-1}
      disabled={Boolean(disabled)}
      aria-disabled={disabled ? true : undefined}
      onClick={select}
      className={className}
    >
      {body}
    </button>
  );
}

export function MenuSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div role="group" aria-label={title}>
      <p className="px-2.5 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-fg-subtle">
        {title}
      </p>
      {children}
    </div>
  );
}

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-border-faint" />;
}
